import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { registerAlertHandler } from '../utils/alertBridge';

const AlertContext = createContext();

export const useAlert = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlert must be used within an AlertProvider');
  }
  return context;
};

// How long the alert stays on screen before auto-dismissing (ms). Kept here so
// GlobalAlert's Snackbar and this timer can't drift apart.
export const ALERT_AUTO_HIDE_MS = 4000;

export const AlertProvider = ({ children }) => {
  const [alert, setAlert] = useState(null);
  const timerRef = useRef(null);

  const clearAlert = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setAlert(null);
  }, []);

  // Backward-compatible signature: setAlert(message, severity = 'success').
  // Every existing call site (48 files) keeps working unchanged.
  const setAlertAction = useCallback((message, severity = 'success') => {
    if (!message) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    setAlert({ message, severity });
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setAlert(null);
    }, ALERT_AUTO_HIDE_MS);
  }, []);

  // Expose setAlert to non-component code (useApiMutation, plain utilities) via
  // the module-level bridge, so API success/error can raise the same alert
  // without prop-drilling. Unregister on unmount to avoid a stale handler.
  useEffect(() => registerAlertHandler(setAlertAction), [setAlertAction]);

  // Clean up a pending timer if the provider unmounts mid-alert.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  return (
    <AlertContext.Provider value={{ alert, setAlert: setAlertAction, clearAlert }}>
      {children}
    </AlertContext.Provider>
  );
};
